import { useEffect, useMemo, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useI18n } from "../i18n/I18nProvider";

// Fix default icon paths for leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const brickIcon = L.divIcon({
  className: "",
  html: '<div style="background:#C5533B;width:22px;height:22px;border:2px solid #1C1C1A;transform:rotate(45deg);box-shadow:2px 2px 0 #1C1C1A"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function ClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function Recenter({ lat, lng, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], zoom ?? map.getZoom(), { animate: true });
  }, [lat, lng, zoom, map]);
  return null;
}

export default function MapPicker({ value, onChange }) {
  const { t } = useI18n();
  const [position, setPosition] = useState(value?.lat ? [value.lat, value.lng] : [52.370216, 4.895168]);
  const [zoom, setZoom] = useState(value?.lat ? 13 : 5);
  const [loadingAddr, setLoadingAddr] = useState(false);
  const debounceRef = useRef(null);

  const markerPosition = useMemo(
    () => (value?.lat != null && value?.lng != null ? [value.lat, value.lng] : null),
    [value?.lat, value?.lng]
  );

  useEffect(() => {
    if (value?.lat != null) {
      setPosition([value.lat, value.lng]);
      setZoom(13);
    }
  }, [value?.lat, value?.lng]);

  const reverseGeocode = async (lat, lng) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoadingAddr(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=12`, {
          headers: { "Accept-Language": "en" },
        });
        const j = await r.json();
        const label = j.display_name || "";
        onChange({ lat, lng, location: label });
      } catch {
        onChange({ lat, lng });
      } finally {
        setLoadingAddr(false);
      }
    }, 300);
  };

  const handleMapClick = ({ lat, lng }) => {
    setPosition([lat, lng]);
    reverseGeocode(lat, lng);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => handleMapClick({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}
    );
  };

  return (
    <div className="border border-[var(--ink-200)]">
      <div className="flex items-center justify-between p-2 bg-[var(--surface-alt)] border-b border-[var(--ink-200)]">
        <div className="label-tech">
          {value?.lat != null ? `${t("map.pin")} ${value.lat.toFixed(4)}, ${value.lng.toFixed(4)}${loadingAddr ? ` · ${t("map.geocoding")}` : ""}` : t("map.clickToPin")}
        </div>
        <button type="button" onClick={useMyLocation} className="label-tech hover:text-[var(--ink)] underline" data-testid="map-use-my-location-btn">
          {t("map.useLocation")}
        </button>
      </div>
      <div style={{ height: 320 }} data-testid="map-picker-container">
        <MapContainer center={position} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; OpenStreetMap'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onClick={handleMapClick} />
          {markerPosition && <Marker position={markerPosition} icon={brickIcon} />}
          <Recenter lat={value?.lat} lng={value?.lng} zoom={13} />
        </MapContainer>
      </div>
    </div>
  );
}
